import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon } from 'lucide-react';
import { TextField } from '../components/forms/TextField';
import { TextAreaField } from '../components/forms/TextAreaField';
import { CheckboxField } from '../components/forms/CheckboxField';
import { Button } from '../components/Button';
import { SweepLoader } from '../components/SweepLoader';
import { SocialIcon } from '../components/SocialIcon';
import { NeedFromAbby } from '../components/NeedFromAbby';
import { Dial } from '../components/Dial';
import { usePageMeta } from '../hooks/usePageMeta';
import { socialLinks } from '../data/site';
import { membershipPlans } from '../data/readings';
import { isValidEmail, isValidPhone } from '../utils/validation';
export function Contact() {
  usePageMeta('Contact', 'Get in touch with True Twelve. Readings held by phone or video, worldwide.');
  const [params] = useSearchParams();
  const plan = membershipPlans.find(p => p.id === params.get('subject'));
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const get = (k: string) => String(d.get(k) ?? '').trim();
    const next: Record<string, string> = {};
    if (!get('name')) next.name = 'Enter your name.';
    if (!isValidEmail(get('email'))) next.email = 'Enter a valid email address.';
    if (get('phone') && !isValidPhone(get('phone'))) next.phone = 'Enter a valid phone number.';
    if (!get('message')) next.message = 'Write a short message.';
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      document.getElementById(`f-${first}`)?.focus();
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 1000);
  }
  return <section aria-labelledby="contact-title" className="gutter grid gap-[clamp(3rem,6vw,9rem)] py-[clamp(3rem,7vw,8rem)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]" data-id="element-653" data-mp="2bxbx3oflhc0m">
      <div data-id="element-654" data-mp="1h7r1zee1shbu">
        <p className="t-label flex items-center gap-3 text-graphite" data-id="element-655" data-mp="1had6f55n6gpu">
          <span className="t-numeral text-gold-deep" data-id="element-656" data-mp="1w48azyyrljv8">V</span>
          <span aria-hidden="true" className="text-gold" data-id="element-657" data-mp="1r6q04316sukl">
            ·
          </span>
          Contact
        </p>
        <h1 id="contact-title" className="t-display mt-6 max-w-[10ch]" data-id="element-658" data-mp="13g2pvb3stsuv">
          Let's talk.
        </h1>
        <p className="t-lead mt-8 max-w-[40ch] text-graphite" data-id="element-659" data-mp="yiu4z2r9napx">Readings held by phone or video, worldwide.</p>
        <dl className="mt-12 space-y-6 border-t border-stone pt-8" data-id="element-660" data-mp="1d7gv7ziflmvx">
          <div data-id="element-661" data-mp="18ffarqb40put">
            <dt className="t-label text-[0.64rem] text-charcoal" data-id="element-662" data-mp="18exh91fu0pql">Email &amp; phone</dt>
            <dd className="mt-2" data-id="element-663" data-mp="kpus78ivbum2">
              <NeedFromAbby data-id="element-664" data-mp="kotesxedqw16">public business email / phone</NeedFromAbby>
            </dd>
          </div>
          <div data-id="element-665" data-mp="fqrsm3lnqglh">
            <dt className="t-label text-[0.64rem] text-charcoal" data-id="element-666" data-mp="fp700jiwl7q9">Follow</dt>
            <dd className="mt-3" data-id="element-667" data-mp="2rd24wfj2onvo">
              <ul className="flex gap-2" data-id="element-668" data-mp="2w5d2vcu95zll">
                {socialLinks.map(s => <li key={s.network} data-id="element-669" data-mp="2w7fzwgttoszt">
                    <a href={s.href} target="_blank" rel="noreferrer" aria-label={`${s.label} ${s.handle}`} className="grid h-11 w-11 place-items-center border border-stone text-charcoal transition-colors duration-150 hover:border-charcoal" data-id="element-670" data-mp="2hgz011wu6b51">
                      <SocialIcon network={s.network} data-id="element-671" data-mp="3a5xla7w99tek" />
                    </a>
                  </li>)}
              </ul>
            </dd>
          </div>
        </dl>
        <Dial className="mt-16 hidden w-[min(100%,22rem)] lg:block" wordmark data-id="element-672" data-mp="313oe9s074i5x" />
      </div>

      <div className="bg-cream p-[clamp(1.75rem,4vw,5rem)]" data-id="element-673" data-mp="3550869q3rpjx">
        {status === 'success' ? <div role="status" className="flex min-h-[24rem] flex-col justify-center" data-id="element-674" data-mp="1po81ulvlecd5">
            <span className="grid h-12 w-12 place-items-center rounded-full border border-gold" data-id="element-675" data-mp="1tr4qat7td2p0">
              <CheckIcon aria-hidden="true" strokeWidth={1.25} className="h-5 w-5 text-charcoal" data-id="element-676" data-mp="29yanjyj6fsxh" />
            </span>
            <h2 className="t-h2 mt-8" data-id="element-677" data-mp="2569cgfxqw891">Message received.</h2>
            <p className="t-lead mt-4 max-w-[40ch] text-graphite" data-id="element-678" data-mp="207y5ccyu4bui">Thank you for reaching out. We'll reply by email soon.</p>
          </div> : <form onSubmit={handleSubmit} noValidate aria-label="Contact form" className="space-y-6" data-id="element-679" data-mp="1vapmsb9ry5sq">
            <h2 className="t-h3" data-id="element-680" data-mp="2swd8a14oz3ph">Send a message</h2>
            {plan && <input type="hidden" name="subject" value={plan.name} data-id="element-681" data-mp="2nxa8ow7hdpow" />}
            <div className="grid gap-5 md:grid-cols-2" data-id="element-682" data-mp="2nvz5axhy6jyb">
              <TextField label="Name" name="name" autoComplete="name" error={errors.name} className="md:col-span-2" data-id="element-683" data-mp="2ixe4owys9y9c" />
              <TextField label="Email" name="email" type="email" autoComplete="email" error={errors.email} data-id="element-684" data-mp="2ewubu5l8tgt0" />
              <TextField label="Phone" name="phone" type="tel" autoComplete="tel" optional error={errors.phone} data-id="element-685" data-mp="17hftamg4v04y" />
            </div>
            <TextAreaField label="Message" name="message" rows={6} error={errors.message} defaultValue={plan ? `I'd like to learn more about the ${plan.name}.` : ''} data-id="element-686" data-mp="3f5k9qle90c90" />
            <CheckboxField name="sms_consent" data-id="element-687" data-mp="3o4fcfni3347k">Okay to text me a reply. Msg &amp; data rates may apply.</CheckboxField>
            <Button type="submit" size="lg" disabled={status === 'submitting'} className="w-full sm:w-auto" data-id="element-688" data-mp="3sys1qe68r9r8">
              {status === 'submitting' ? <>
                  <SweepLoader label="Sending" data-id="element-689" data-mp="6nxlenxkiaid" /> Sending
                </> : 'Send Message'}
            </Button>
          </form>}
      </div>
    </section>;
}